import * as m from '@staiter/i18n';
import type {
  ApprovalSignature,
  WoExecutionStopCause,
  WorkOrderStatus as WoState,
} from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import {
  AlertOctagon,
  AlertTriangle,
  Copy,
  FileText,
  History,
  Hourglass,
  Info,
  Plus,
  RefreshCw,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { Tier3SendAction } from '~/components/approvals';
import { Breadcrumbs, ErrorState, PageHeader, Skeleton, Table } from '~/components/data';
import { EntityAuditPanel } from '~/components/domain';
import { ProjectRefChip } from '~/components/domain/project/ProjectRefChip';
import { useToast } from '~/components/feedback';
import { Button, Combobox, ConfirmDialog, Dialog, Input } from '~/components/primitives';
import {
  JobCardsTable,
  ManufacturePreviewDialog,
  WoStateMachine,
  WoTimeline,
  WoWarningsPanel,
} from '~/components/work-orders';
import {
  cancelWorkOrder,
  completeWorkOrder,
  fetchManufacturePreview,
  fetchWoApprovers,
  fetchWoSles,
  fetchWoTimeline,
  fetchWoWarnings,
  fetchWorkOrder,
  pauseWorkOrder,
  releaseWorkOrder,
  resumeWorkOrder,
  startWorkOrder,
} from '~/lib/api/work-orders';
import { AuditHighlightSearch } from '~/lib/audit/highlight-search';
import { woErrorToast, woStateLabel, woStateTitle } from '~/lib/errors/wo-codes';
import { formatDate, formatDateTime, formatQty } from '~/lib/format';
import { getRoleLabel } from '~/lib/rbac';
import { useWoStream } from '~/lib/sse/useRunStream';
import { woCancelMode, woCancelOpenWhenStopped } from '~/lib/work-orders/cancel-mode';
import { woShortcutAction } from '~/lib/work-orders/shortcuts';
import { useAuthStore } from '~/store/auth';
import shared from './_auth._shared.module.css';
import styles from './_auth.work-orders.$id.index.module.css';

type Tab = 'overview' | 'materials' | 'job_cards' | 'warnings' | 'timeline' | 'audit' | 'sles';

// U6.2 — tab в URL ?tab=…; F5 сохраняет выбранный таб, URL можно шарить.
const TabEnum = z.enum([
  'overview',
  'materials',
  'job_cards',
  'warnings',
  'timeline',
  'audit',
  'sles',
]);
// F105 (С-I): `highlight` — запись журнала, в которую ведёт подпись (вкладка «Аудит»).
const SearchSchema = z
  .object({ tab: TabEnum.optional().catch(undefined) })
  .merge(AuditHighlightSearch);

// EFE-4 Pass 90: rename .tsx → .index.tsx (parent-without-Outlet bug, как FE-294 / CP-detail).
/** Название с дефисом («FG-MEMBRANE») не рвётся на дефисе посреди фразы — неразрывный дефис U+2011. */
const nbHyphen = (text: string): string => text.replace(/-/g, '‑');

/** F110 круг 3 (UX): итог закрытого процесса заказа — словами. */
const STOP_CAUSE_LABEL: Record<WoExecutionStopCause, () => string> = {
  completed: m.wo_stop_cause_completed,
  failed: m.wo_stop_cause_failed,
  cancelled: m.wo_stop_cause_cancelled,
  terminated: m.wo_stop_cause_terminated,
  timed_out: m.wo_stop_cause_timed_out,
  not_found: m.wo_stop_cause_not_found,
};

export const Route = createFileRoute('/_auth/work-orders/$id/')({
  staticData: { minMainWidth: 960 },
  component: WorkOrderDetail,
  validateSearch: (s: Record<string, unknown>) => SearchSchema.parse(s),
});

function tabsList(): { id: Tab; label: string }[] {
  return [
    { id: 'overview', label: m.route_wo_detail_tab_overview() },
    { id: 'materials', label: m.route_wo_detail_tab_materials() },
    { id: 'job_cards', label: m.route_wo_detail_tab_job_cards() },
    { id: 'warnings', label: m.route_wo_detail_tab_warnings() },
    { id: 'timeline', label: m.route_wo_detail_tab_timeline() },
    { id: 'audit', label: m.route_wo_detail_tab_audit() },
    { id: 'sles', label: m.route_wo_detail_tab_sles() },
  ];
}
const TAB_IDS: Tab[] = [
  'overview',
  'materials',
  'job_cards',
  'warnings',
  'timeline',
  'audit',
  'sles',
];

function WorkOrderDetail(): JSX.Element {
  const { id } = useParams({ from: '/_auth/work-orders/$id/' });
  const qc = useQueryClient();
  /*
   * F110 (С-I, гейты кругов 1–2): «исполнение остановлено» — признак с сервера
   * (`wo.execution_stopped`), а не память одного браузера: список, коллеги и администратор видят то
   * же. Номер обращения последней ошибки действия держим в памяти — он нужен в оговорке.
   */
  const [stoppedTrace, setStoppedTrace] = useState<string | null>(null);
  const userRole = useAuthStore((st) => st.user?.role ?? null);
  const isAdminViewer = userRole === 'admin' || userRole === 'company_admin';
  const myId = useAuthStore((st) => st.user?.user_id ?? st.user?.id ?? null);
  const toast = useToast();
  // U6.2 — URL-state для tab.
  const nav = useNavigate({ from: '/work-orders/$id/' });
  const search = useSearch({ from: '/_auth/work-orders/$id/' });
  const tab: Tab = search.tab ?? 'overview';
  const setTab = (next: Tab, highlight?: string | null): void => {
    void nav({ search: { tab: next, ...(highlight ? { highlight } : {}) }, replace: true });
  };
  const [releaseDlg, setReleaseDlg] = useState(false);
  const [cancelDlg, setCancelDlg] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  // F110 (С-I, TCA-B9): отмена — уровень 2, нужна подпись согласующего (раньше диалог её не слал,
  // и отмена с карточки всегда падала «Требуется подпись уровня 2»).
  const [cancelApproverId, setCancelApproverId] = useState('');
  // F110 круг 3: кандидаты — члены компании заказа с правом согласования (`GET /:id/approvers`), роль —
  // роль в этой компании. Общий список пользователей предлагал наблюдателей и сервисные аккаунты, а
  // роль показывал глобальную: выбранного сервер отклонял уже после нажатия.
  const approversQuery = useQuery({
    queryKey: ['work-orders', id, 'approvers', ''],
    queryFn: () => fetchWoApprovers(id),
    enabled: cancelDlg,
  });
  const cancelApprovers = approversQuery.data?.items ?? [];
  // Круг 4: поиск идёт на сервер (`q`), выбранный берётся из любой загруженной выборки.
  type ApproverCandidate = { id: string; email: string; full_name: string | null; role: string };
  const approverById = useRef(new Map<string, ApproverCandidate>());
  for (const u of cancelApprovers) approverById.current.set(u.id, u);
  const cancelApprover = cancelApproverId
    ? (approverById.current.get(cancelApproverId) ?? null)
    : null;
  const cancelApproverRole = cancelApprover?.role ?? null;
  const approverRoles = new Intl.ListFormat(m.getLanguageTag(), { type: 'disjunction' }).format(
    (approversQuery.data?.required_roles ?? []).map((r) => `«${getRoleLabel(r)}»`),
  );
  // Круг 3 гейта (UX): сигналы заказа не исключают инициатора из подписантов
  // (`actor_excluded_from_signers: false`), и экран это говорит, а не прячет (решение — NC-112).
  const actorIsCandidate = myId !== null && cancelApprovers.some((u) => u.id === myId);
  const approverOption = useCallback(
    (u: ApproverCandidate) => ({
      value: u.id,
      label: m.route_wo_detail_cancel_approver_option({
        name: u.full_name ?? u.email,
        role: getRoleLabel(u.role),
      }),
      // Круг 4 (UI): «вы» — приглушённым хвостом, не частью подписи роли.
      ...(u.id === myId ? { suffix: m.route_wo_detail_cancel_approver_self_suffix() } : {}),
      ...(u.full_name ? { hint: u.email } : {}),
    }),
    [myId],
  );
  const loadApprovers = useCallback(
    async (q: string) => {
      const term = q.trim();
      const res = await qc.fetchQuery({
        queryKey: ['work-orders', id, 'approvers', term],
        queryFn: () => fetchWoApprovers(id, term || undefined),
        staleTime: 30_000,
      });
      for (const u of res.items) approverById.current.set(u.id, u);
      // Круг 4 (UX): своя строка — в конце выдачи: первой и подсвеченной её выбирал Enter.
      const ordered = [
        ...res.items.filter((u) => u.id !== myId),
        ...res.items.filter((u) => u.id === myId),
      ];
      return ordered.map(approverOption);
    },
    [id, qc, approverOption, myId],
  );
  const [pauseDlg, setPauseDlg] = useState(false);
  // F110 круг 12 (UX, BLOCKER): завершение с клавиши — через подтверждение.
  const [completeDlg, setCompleteDlg] = useState(false);
  // U9.3 — help overlay (?) со списком shortcuts.
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);

  const woQuery = useQuery({
    queryKey: ['work-orders', id],
    queryFn: () => fetchWorkOrder(id),
    retry: false,
  });
  const wo = woQuery.data;
  // С-I3 Р.5 — «Проверить снова»: перепроверка мимо окна «Temporal не отвечает».
  const recheckMut = useMutation({
    mutationFn: () => fetchWorkOrder(id, { recheck: true }),
    onSuccess: (fresh) => qc.setQueryData(['work-orders', id], fresh),
  });

  // Bundle warnings с резолвленными items/batches/warehouses — загружаем только когда
  // tab=warnings открыт (lazy). Без bundle WoWarningsPanel показывает UUID вместо имён.
  // Если bundle-endpoint недоступен — inline fallback (wo.warnings) с UUID, но без toast.
  const warningsBundleQuery = useQuery({
    queryKey: ['work-orders', id, 'warnings'],
    queryFn: () => fetchWoWarnings(id),
    enabled: Boolean(wo),
    retry: false,
    meta: { suppressErrorToast: true },
  });

  // U8.2 — 404 → redirect на list с toast.
  useEffect(() => {
    if (woQuery.isError && woQuery.error instanceof Error) {
      const err = woQuery.error as { status?: number };
      if (err.status === 404) {
        void nav({ to: '/work-orders', search: { error: 'wo_not_found' } as never });
      }
    }
  }, [woQuery.isError, woQuery.error, nav]);

  // U13.2 — batch debounced invalidate: 5 событий за 100ms = один invalidate.
  const invalidateTimer = useState<{ handle: ReturnType<typeof setTimeout> | null }>(() => ({
    handle: null,
  }))[0];
  const queueInvalidate = (): void => {
    if (invalidateTimer.handle) clearTimeout(invalidateTimer.handle);
    invalidateTimer.handle = setTimeout(() => {
      qc.invalidateQueries({ queryKey: ['work-orders', id] });
      invalidateTimer.handle = null;
    }, 200);
  };

  const sseState = useWoStream(id, {
    onEvent: (ev) => {
      if (
        ev.kind === 'wo.status_changed' ||
        ev.kind === 'wo.sle_generated' ||
        ev.kind === 'wo.completed'
      ) {
        queueInvalidate();
      }
      if (ev.kind === 'wo.warning_raised') {
        // F-A.24 — structural event; не рендерим сырые поля, invalidate bundle
        // и показываем тип через i18n.
        qc.invalidateQueries({ queryKey: ['work-orders', id, 'warnings'] });
        toast.push({
          variant: 'warning',
          title: m.tab_warnings(),
          description: m.wo_warning_type_label({ type: ev.warning_type }),
        });
      }
    },
  });

  const state: WoState | null = sseState.state ?? wo?.state ?? null;

  // Manufacture preview (loaded when opening release dialog).
  // U10.4 — убираем releaseDlg из queryKey + staleTime 30s: переоткрытие dialog не триггерит
  // refetch, если state не изменился. SSE wo.status_changed invalidate переигрывает.
  const { data: preview, isFetching: previewLoading } = useQuery({
    queryKey: ['wo-preview', id],
    queryFn: () => fetchManufacturePreview(id),
    enabled: releaseDlg,
    staleTime: 30_000,
  });

  // U0.3 — retry:false + meta.suppressErrorToast чтобы 404/500 не бросали toast через
  // глобальный QueryCache.onError. Error state рендерим inline в tab-panel (U8.1).
  const actionsStopped = wo?.execution_stopped === true;
  /**
   * С-I2 Р.1 (TCA-B9): `planned` с остановленным процессом сервер отменяет в базе (B82) — выход
   * из тупика. Заперт только выпуск; «Отменить» — тот же диалог с согласующим.
   */
  const stoppedPlanned = actionsStopped && woCancelOpenWhenStopped(state);
  // § 4.1: выключенное действие с причиной — фокусируемо; нажатие ведёт к оговорке.
  const focusStoppedNotice = (): void => document.getElementById('wo-process-stopped')?.focus();
  const stoppedProps = actionsStopped
    ? {
        disabled: true,
        focusableWhenDisabled: true,
        onDisabledClick: focusStoppedNotice,
        'aria-describedby': 'wo-actions-stopped',
      }
    : {};
  // Круг 3 гейта (UX): когда и как остановилось — время закрытия и итог процесса из Temporal.
  const stopCause = wo?.execution_stop_cause ?? null;
  const stopCauseLabel = stopCause ? STOP_CAUSE_LABEL[stopCause]() : null;
  const stoppedWhen = !stopCauseLabel
    ? null
    : wo?.execution_stopped_at
      ? m.route_wo_detail_process_stopped_when({
          at: formatDateTime(wo.execution_stopped_at),
          cause: stopCauseLabel,
        })
      : m.route_wo_detail_process_stopped_cause_only({ cause: stopCauseLabel });
  const copyStoppedRef = (ref: string): void => {
    void navigator.clipboard.writeText(ref).then(
      () => toast.push({ variant: 'success', title: m.common_toast_copy_success() }),
      () => toast.push({ variant: 'error', title: m.common_toast_copy_failed() }),
    );
  };
  /** Круг 3 гейта (UI): при остановке подсказки клавиш (`title`) снимаются вместе с `kbd`. */
  // Круг 14 (UI): клавиша — для чтеца через `aria-keyshortcuts`, буква колпачка из имени кнопки убрана.
  const shortcutTitle = (title: string, key: string) =>
    actionsStopped ? {} : { title, 'aria-keyshortcuts': key };

  /*
   * F104 (NC-55): история переходов грузится сразу, а не при открытии вкладки — из неё степпер
   * берёт пройденные узлы, плитки «Факт» — время, оговорка отмены — кто, когда и почему.
   */
  const timelineQuery = useQuery({
    queryKey: ['wo-timeline', id],
    queryFn: () => fetchWoTimeline(id),
    retry: false,
    meta: { suppressErrorToast: true },
  });
  const timeline = timelineQuery.data;
  const timelineItems = timeline?.items ?? [];
  /** Состояния, в которых заказ побывал; `null` — истории нет, степпер рисует по-старому. */
  const visitedStates: ReadonlySet<WoState> | null =
    timelineItems.length > 0
      ? new Set(
          timelineItems.flatMap((e) =>
            [e.from_state, e.to_state].filter((x): x is string => !!x),
          ) as WoState[],
        )
      : null;
  /*
   * F104 (NC-55; B85): плитки «Факт старт / конец» — из ленты событий. Колонки `actual_start/end`
   * пишутся отдельно от строки ленты, и на стенде плитка показывала 12:34 при 11:50 в истории.
   * Колонка — запасной источник, пока история не пришла или пуста.
   */
  const firstStartAt = timelineItems.find((e) => e.to_state === 'in_progress')?.changed_at;
  const lastCompletedAt = timelineItems.findLast((e) => e.to_state === 'completed')?.changed_at;
  const factStart = firstStartAt ?? wo?.actual_start_at ?? null;
  /** F104 (круг 3, UX): отмена создала обратный документ выпуска, но его не провели. */
  const reversalPending =
    wo?.state === 'cancelled' &&
    !!wo.cancel_output?.reversal &&
    !wo.cancel_output.reversal.is_submitted;
  const factEnd = lastCompletedAt ?? wo?.actual_end_at ?? null;
  const cancelEntry = timelineItems.findLast((e) => e.to_state === 'cancelled');

  const slesQuery = useQuery({
    queryKey: ['wo-sles', id],
    queryFn: () => fetchWoSles(id),
    enabled: tab === 'sles',
    retry: false,
    meta: { suppressErrorToast: true },
  });
  const sles = slesQuery.data;

  const invalidate = () => qc.invalidateQueries({ queryKey: ['work-orders', id] });

  /**
   * U0.2 — после merge W0.1 backend signal-aliases возвращают полный WoDetail-compat shape.
   * Обновляем кэш через setQueryData → меньше flicker и нет лишнего GET-запроса.
   * Для безопасности на случай что backend вернул частичный shape — дополнительно invalidate.
   */
  const applySignalResult = (res: unknown) => {
    if (res && typeof res === 'object' && 'id' in res && 'state' in res) {
      qc.setQueryData(['work-orders', id], res);
    }
    invalidate();
  };

  // U10.1 — опции override/alternates/signatures передаются из ManufacturePreviewDialog.onConfirm.
  /*
   * F110 (С-I, гейты кругов 1–3): ошибка действия над заказом — тост с номером обращения. На
   * «исполнение остановлено» (`WORKFLOW_CLOSED`) карточка перечитывается: оговорку и выключенные
   * действия даёт признак сервера, тост лишь коротко говорит, что действие не выполнено.
   */
  const onActionError = (title: string) => (err: Error) => {
    const t = woErrorToast(err);
    if (t.processStopped) {
      // Постоянное сообщение несёт оговорка карточки (признак с сервера) — тост короткий.
      setStoppedTrace(t.traceId ?? null);
      qc.invalidateQueries({ queryKey: ['work-orders', id] });
    }
    toast.push({
      variant: 'error',
      title,
      description: t.processStopped ? m.route_wo_detail_toast_process_stopped() : t.description,
      ...(t.traceId ? { traceId: t.traceId } : {}),
    });
  };

  const releaseMut = useMutation({
    // F110: тост ошибки — свой (woErrorMessage), общий тост MutationCache его дублировал.
    meta: { suppressErrorToast: true },
    mutationFn: (opts: {
      override?: boolean;
      alternates?: Record<string, string>;
      signatures?: ApprovalSignature[];
      reason?: string;
    }) => releaseWorkOrder(id, opts),
    onSuccess: (res) => {
      toast.push({ variant: 'success', title: m.route_wo_detail_toast_released() });
      setReleaseDlg(false);
      applySignalResult(res);
    },
    onError: onActionError(m.route_wo_detail_toast_release_failed()),
  });

  const startMut = useMutation({
    // F110: тост ошибки — свой (woErrorMessage), общий тост MutationCache его дублировал.
    meta: { suppressErrorToast: true },
    mutationFn: () => startWorkOrder(id),
    onSuccess: (res) => {
      toast.push({ variant: 'success', title: m.route_wo_detail_toast_started() });
      applySignalResult(res);
    },
    onError: onActionError(m.route_wo_detail_toast_start_failed()),
  });

  const pauseMut = useMutation({
    // F110: тост ошибки — свой (woErrorMessage), общий тост MutationCache его дублировал.
    meta: { suppressErrorToast: true },
    mutationFn: (reason: string) => pauseWorkOrder(id, reason),
    onSuccess: (res) => {
      toast.push({ variant: 'success', title: m.route_wo_detail_toast_paused() });
      setPauseDlg(false);
      applySignalResult(res);
    },
    onError: onActionError(m.route_wo_detail_toast_pause_failed()),
  });

  const resumeMut = useMutation({
    // F110: тост ошибки — свой (woErrorMessage), общий тост MutationCache его дублировал.
    meta: { suppressErrorToast: true },
    mutationFn: () => resumeWorkOrder(id),
    onSuccess: (res) => {
      toast.push({ variant: 'success', title: m.route_wo_detail_toast_resumed() });
      applySignalResult(res);
    },
    onError: onActionError(m.route_wo_detail_toast_resume_failed()),
  });

  const completeMut = useMutation({
    // F110: тост ошибки — свой (woErrorMessage), общий тост MutationCache его дублировал.
    meta: { suppressErrorToast: true },
    mutationFn: () => completeWorkOrder(id),
    onSuccess: (res) => {
      // Круг 14 (UX): завершение создаёт черновик записи «Производство» — тост говорит, где он.
      toast.push({
        variant: 'success',
        title: m.route_wo_detail_toast_completed(),
        // Круг 15 (UX): действие «Складские записи» вело на вкладку проводок, а у черновика их ноль —
        // тост говорит, где черновик, без ложного перехода (ссылка на сам черновик — NC-118).
        description: m.route_wo_detail_toast_completed_draft(),
      });
      applySignalResult(res);
    },
    onError: onActionError(m.route_wo_detail_toast_complete_failed()),
  });

  // F110 круг 3: «Не отменять» и крестик закрывают одинаково — со сбросом полей; раньше «Не отменять»
  // оставлял причину и согласующего, и при следующем открытии диалог был заполнен прошлым.
  const closeCancelDlg = (): void => {
    setCancelDlg(false);
    setCancelReason('');
    setCancelApproverId('');
  };

  const cancelBlockedReason =
    cancelReason.trim().length < 10
      ? m.route_wo_detail_cancel_reason_missing()
      : !cancelApproverId
        ? m.route_wo_detail_cancel_approver_missing()
        : null;

  const cancelTier2Mut = useMutation({
    // F110: тост ошибки — свой (woErrorMessage), общий тост MutationCache его дублировал.
    meta: { suppressErrorToast: true },
    mutationFn: (reason: string) =>
      cancelWorkOrder(id, {
        reason,
        ...(cancelApproverId && cancelApproverRole
          ? {
              signatures: [
                {
                  user_id: cancelApproverId,
                  role: cancelApproverRole as ApprovalSignature['role'],
                  signed_at: new Date().toISOString(),
                  reason,
                },
              ],
            }
          : {}),
      }),
    onSuccess: (res) => {
      toast.push({ variant: 'success', title: m.route_wo_detail_toast_cancelled_t2() });
      setCancelDlg(false);
      setCancelReason('');
      setCancelApproverId('');
      applySignalResult(res);
    },
    onError: onActionError(m.route_wo_detail_toast_cancel_failed_t2()),
  });

  // F110 круг 15 (UX): склады, без которых черновик выпуска нельзя провести.
  const missingWarehouses = [
    wo && !wo.wip_warehouse_id ? m.route_wo_detail_field_wip_wh() : null,
    wo && !wo.fg_warehouse_id ? m.route_wo_detail_field_fg_wh() : null,
  ]
    .filter((v): v is string => v !== null)
    // в середине фразы — со строчной: «не указаны склады — склад НЗП, …»
    .map((label) => label.charAt(0).toLocaleLowerCase(m.getLanguageTag()) + label.slice(1));

  const completeBlocked = missingWarehouses.length > 0;
  // Круг 17 (UX): в «Обзоре» пустой склад заказа в работе — «не указан» тоном warn: он мешает завершению.
  const missingWarehouseNote =
    state === 'in_progress' ? m.route_wo_detail_warehouse_not_set() : null;

  // F110 круг 13 (UX): пока сигнал в пути, действия выключены — второй клик или клавиша не уходят.
  const signalPending =
    releaseMut.isPending ||
    startMut.isPending ||
    completeMut.isPending ||
    resumeMut.isPending ||
    pauseMut.isPending ||
    cancelTier2Mut.isPending;
  const actionProps = actionsStopped ? stoppedProps : { disabled: signalPending };

  // Keyboard shortcuts: r=release, s=start, c=complete
  // U9.1 — guard на custom Select/Combobox/contenteditable + открытые диалоги.
  // U9.2 — guard на любой открытый dialog (release, cancel, pause).
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest?.('[role="combobox"], [role="listbox"], [contenteditable="true"]'))
        return;
      if (target?.closest?.('dialog, [role="dialog"]')) return;
      if (releaseDlg || cancelDlg || pauseDlg || completeDlg) return;
      // Круг 13 (UX): сигнал в пути — второй не отправляем (два быстрых «S» давали успех и следом ошибку).
      if (signalPending) return;
      // F110 круг 12 (UX, BLOCKER): без модификаторов и по `e.code` — см. `woShortcutAction`.
      // При остановленном исполнении действия выключены — клавиши тоже.
      if (actionsStopped) return;
      const action = woShortcutAction(e, state);
      if (action === 'release') setReleaseDlg(true);
      else if (action === 'start') startMut.mutate();
      else if (action === 'resume') resumeMut.mutate();
      else if (action === 'confirm-complete') setCompleteDlg(true);
      // U9.3 — `?` открывает help overlay со всеми shortcuts.
      else if (action === 'help') setShortcutHelpOpen(true);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [
    state,
    actionsStopped,
    signalPending,
    startMut,
    resumeMut,
    releaseDlg,
    cancelDlg,
    pauseDlg,
    completeDlg,
  ]);

  // Canonical WoDetail.cost.variance — unknown (JSONB). Для баннера ожидаем number/string.
  const costVarianceRaw = wo?.cost?.variance;
  const costVariance: string | null =
    typeof costVarianceRaw === 'number' || typeof costVarianceRaw === 'string'
      ? String(costVarianceRaw)
      : null;
  const varianceSign = costVariance
    ? Number(costVariance) > 0
      ? 'neg'
      : Number(costVariance) < 0
        ? 'pos'
        : null
    : null;

  return (
    <div className={shared.page}>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.nav_work_orders(), to: '/work-orders' },
              { label: wo ? wo.code : '…' },
            ]}
          />
        }
        title={
          wo
            ? m.route_wo_detail_title_with_code({ code: wo.code })
            : m.route_wo_detail_title_fallback()
        }
        description={
          wo
            ? // FE-65: subtitle hierarchy — item name bold + stat chips row instead of wall-of-info.
              // F104 (NC-55; гейт F110, круг 10, UI MAJOR): у отменённого заказа шапка без `opacity` —
              // 0,7 поверх `--text-muted` давало ≈ 2,3:1; что заказ отменён, говорят статус и оговорка.
              (() => {
                const itemSku = (wo as { item_sku?: string | null }).item_sku;
                const projectId = (wo as { project_id?: string | null }).project_id ?? null;
                return (
                  <div className={styles.subtitle}>
                    <span className={styles.subtitleItem}>
                      <strong className={styles.subtitleName}>
                        {wo.item_name ?? wo.item_id.slice(0, 8)}
                      </strong>
                      {itemSku && <span className={styles.skuChip}>{itemSku}</span>}
                    </span>
                    <div className={styles.subtitleStats}>
                      {/* Круг 1 (UI): подпись — шрифтом интерфейса, моно — только число с единицей (§ 2.1). */}
                      <span className={styles.statChip}>
                        {m.route_wo_detail_qty_ordered()}{' '}
                        <span className={styles.statValue}>{formatQty(wo.qty, wo.item_uom)}</span>
                      </span>
                      <span className={styles.statChip}>
                        {m.route_wo_detail_qty_produced()}{' '}
                        <span className={styles.statValue}>
                          {formatQty(wo.qty_produced, wo.item_uom)}
                        </span>
                        {/* Круг 3 (UX): выпуск ещё на складе — сторно в черновике. */}
                        {reversalPending && (
                          <span className={styles.statWarn}>
                            {' '}
                            · {m.route_wo_detail_produced_reversal_draft()}
                          </span>
                        )}
                      </span>
                      {projectId ? <ProjectRefChip projectId={projectId} /> : null}
                    </div>
                  </div>
                );
              })()
            : undefined
        }
        actions={
          /*
            Раунд 4 гейта, ОБЕ половины: «индикатор SSE вынесен из плашки, но не из
            строки состояния: между бэйджем „Отменён“ и рельсом висит немаркированная
            зелёная точка, читаемая как первый шаг маршрута» (UI); «место индикатора
            связи — хром страницы» (UX).
            Точка уехала в шапку — туда, где живут действия страницы, а не свойства
            заказа, — и получила ВИДИМОЕ слово рядом: одним цветом состояние не
            кодируется (§4.1b).
          */
          <div className={styles.headerAside}>
            <span
              role="status"
              aria-label={m.route_wo_detail_sse_aria({ connection: sseState.connection })}
              title={m.route_wo_detail_sse_title({ connection: sseState.connection })}
              className={styles.sseChip}
            >
              <span
                className={[
                  styles.sseIndicator,
                  sseState.connection === 'open'
                    ? styles.sseIndicatorOpen
                    : sseState.connection === 'reconnecting' || sseState.connection === 'connecting'
                      ? styles.sseIndicatorPending
                      : styles.sseIndicatorClosed,
                ].join(' ')}
                aria-hidden="true"
              />
              {sseState.connection === 'open'
                ? m.route_wo_detail_sse_word_open()
                : sseState.connection === 'reconnecting' || sseState.connection === 'connecting'
                  ? m.route_wo_detail_sse_word_pending()
                  : m.route_wo_detail_sse_word_closed()}
            </span>
            {wo && (wo.state === 'planned' || wo.state === 'draft') ? (
              // EFE-4 Pass 90: edit CTA visible только в editable states.
              <Button
                variant="secondary"
                onClick={() => nav({ to: '/work-orders/$id/edit', params: { id } })}
              >
                {m.common_button_edit()}
              </Button>
            ) : null}
          </div>
        }
      />

      {wo && state && (
        <>
          {/* U5.1 + FE-P3-6 (pass3): pill-style state heading c color + icon + label
              per a11y rule. WO статусы (released/paused_external/closed) не покрыты
              canonical StatusBadge enum — кастомный pill с правильными labels. */}
          <div className={styles.stateRow}>
            {/*
              Раунд 3 гейта T2A/SR, ОБЕ половины независимо: «зелёная точка внутри
              красной плашки „Отменён“ — терминальное danger-состояние несёт маркер
              успеха без подписи» (UX); «на бэйдже „Отменён“ два маркера
              противоположной семантики» (UI).

              Индикатор связи и статус заказа — разные факты, и в одной пилюле они
              читаются как один. Связь живёт СНАРУЖИ плашки: она про страницу, а не
              про заказ.
            */}
            <div
              className={styles.stateHeading}
              // F104: статус заказа — `data-status`, как у `StatusBadge`; `data-state` — маркер состояния
              // взаимодействия для сторожа заливок (`fe-state-fill-semantic`).
              data-status={state}
              {...(actionsStopped ? { 'data-stopped': 'true' } : {})}
              // Круг 9 (UI): живая область — здесь, у текста, который меняется на каждом переходе.
              role="status"
            >
              <span className={styles.stateBadgeDot} aria-hidden="true" />
              {woStateLabel(state)}
              {/* F110 круг 2 (UX): остановка — состояние blocked, причина в самом статусе. */}
              {actionsStopped && ` · ${m.route_wo_detail_state_stopped_suffix()}`}
            </div>
            <WoStateMachine
              current={state}
              stopped={actionsStopped}
              visited={visitedStates}
              cancelledFrom={
                (wo as { cancelled_from?: string | null }).cancelled_from as
                  | typeof state
                  | null
                  | undefined
              }
            />
          </div>

          {/* Круг 3 гейта (UX): конечный «Отменён» — нейтральная оговорка, остановка — тоном
              опасности, как и пилюля. Значок — Lucide (§ 5), не символ шрифта. */}
          {/* F104 (NC-55): оговорка отвечает, кто отменил, по какому праву, когда и почему, и что делать
              дальше — «Действия недоступны» оставляло в тупике. Тон — инертный, как пилюля (§ 4.4). */}
          {state === 'cancelled' && (
            // Круг 4 (обе половины): открытый хвост сторно — тон warn и треугольник (§ 4.1b «конечное
            // состояние с открытым хвостом»); пилюля «Отменён» остаётся инертной.
            <div
              className={styles.pageNotice}
              data-tone={reversalPending ? 'warn' : 'inert'}
              role="status"
              aria-live="polite"
            >
              {reversalPending ? (
                <AlertTriangle size={16} aria-hidden="true" className={styles.noticeIcon} />
              ) : (
                <Info size={16} aria-hidden="true" className={styles.noticeIcon} />
              )}
              <div className={styles.noticeBody}>
                {/* Круг 3 (UX, MAJOR): незакрытое сторно — первым словом оговорки, не справкой в конце. */}
                <p className={styles.noticeText}>
                  {reversalPending
                    ? m.route_wo_detail_cancelled_lead_reversal_pending()
                    : m.route_wo_detail_cancelled_lead()}
                </p>
                {/* Кто и когда, кто согласовал, почему, что стало с выпуском — сеткой «подпись — значение»
                    (круг 2, UI: продолжение строки вставало на линию подписей и читалось новым полем). Имя и
                    роль, время — тем же набором, что в истории состояний (круг 2, UI: одна отмена дважды и
                    по-разному). Строка «Выпуск» (круг 2, UX MAJOR): у отменённого из «Завершён» шапка
                    показывает «Произведено 0 шт» — здесь сказано, что стало с выпуском. */}
                {cancelEntry && (
                  <dl className={styles.noticeFacts}>
                    <dt>{m.route_wo_detail_cancelled_label_at()}</dt>
                    <dd>
                      <span className={styles.factTime}>
                        {formatDateTime(cancelEntry.changed_at)}
                      </span>
                    </dd>
                    {cancelEntry.changed_by_name && (
                      <>
                        <dt>{m.route_wo_detail_cancelled_label_by()}</dt>
                        <dd>
                          <span className={styles.factName}>{cancelEntry.changed_by_name}</span>
                          {cancelEntry.changed_by_role && (
                            <span className={styles.factRole}>
                              {' '}
                              ({getRoleLabel(cancelEntry.changed_by_role)})
                            </span>
                          )}
                        </dd>
                      </>
                    )}
                    {(cancelEntry.signers ?? []).length > 0 && (
                      <>
                        <dt>{m.route_wo_detail_cancelled_label_signed()}</dt>
                        <dd>
                          {(cancelEntry.signers ?? []).map((sg) => (
                            <span key={sg.user_id} className={styles.factLine}>
                              <span className={styles.factName}>
                                {sg.name ?? sg.user_id.slice(0, 8)}
                              </span>
                              <span className={styles.factRole}> ({getRoleLabel(sg.role)})</span>
                            </span>
                          ))}
                        </dd>
                      </>
                    )}
                    {cancelEntry.reason && (
                      <>
                        <dt>{m.route_wo_detail_cancelled_label_reason()}</dt>
                        <dd>{cancelEntry.reason}</dd>
                      </>
                    )}
                    {(cancelEntry.from_state === 'completed' ||
                      cancelEntry.from_state === 'closed') &&
                      wo.cancel_output && (
                        <>
                          <dt>{m.route_wo_detail_cancelled_label_output()}</dt>
                          <dd>
                            {wo.cancel_output.reversal &&
                            !wo.cancel_output.reversal.is_submitted ? (
                              // Круг 3 (UX, MAJOR): сторно-черновик — открытый хвост, тоном needs_review (▲),
                              // с тем, кто его проводит (роли — из матрицы прав, с сервера).
                              // Круг 4 (UI): значок — своей колонкой, вторая строка под текстом; янтарь — только у
                              // «сторно не проведено», остальное — тоном значения.
                              <span className={styles.factWarnRow}>
                                <AlertTriangle
                                  size={12}
                                  aria-hidden="true"
                                  className={styles.factWarnIcon}
                                />
                                <span>
                                  <span className={styles.factWarn}>
                                    {m.route_wo_detail_cancelled_output_reversal_pending()}
                                  </span>{' '}
                                  <a
                                    className={`link-inline ${styles.factLink}`}
                                    href={`/stock-entries/${wo.cancel_output.reversal.id}`}
                                  >
                                    {wo.cancel_output.reversal.voucher_no}
                                  </a>
                                  {wo.cancel_output.submit_roles.length > 0 &&
                                    ` · ${m.route_wo_detail_cancelled_output_posters({
                                      roles: new Intl.ListFormat(m.getLanguageTag(), {
                                        type: 'disjunction',
                                      }).format(wo.cancel_output.submit_roles.map(getRoleLabel)),
                                    })}`}
                                </span>
                              </span>
                            ) : wo.cancel_output.reversal ? (
                              <>
                                {m.route_wo_detail_cancelled_output_reversed()}{' '}
                                <a
                                  className={`link-inline ${styles.factLink}`}
                                  href={`/stock-entries/${wo.cancel_output.reversal.id}`}
                                >
                                  {wo.cancel_output.reversal.voucher_no}
                                </a>
                              </>
                            ) : wo.cancel_output.manufacture ? (
                              <>
                                {m.route_wo_detail_cancelled_output_not_reversed()}{' '}
                                <a
                                  className={`link-inline ${styles.factLink}`}
                                  href={`/stock-entries/${wo.cancel_output.manufacture.id}`}
                                >
                                  {wo.cancel_output.manufacture.voucher_no}
                                </a>
                              </>
                            ) : (
                              // Круг 3 (UX, MAJOR): «движений нет» не проверено — смотрим только документ выпуска.
                              m.route_wo_detail_cancelled_output_none()
                            )}
                          </dd>
                        </>
                      )}
                  </dl>
                )}
                {/* Круг 1 (UI, MAJOR): первая кнопка — с рамкой, её рамка — на линии текста оговорки. */}
                <div className={`${styles.noticeActions} ${styles.noticeActionsFlush}`}>
                  {/* Круг 3 (UX, MAJOR): при непроведённом сторно первый шаг — сторно, не новый заказ. */}
                  {reversalPending && wo.cancel_output?.reversal && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        window.location.assign(`/stock-entries/${wo.cancel_output?.reversal?.id}`);
                      }}
                    >
                      <FileText size={16} aria-hidden="true" />
                      {m.route_wo_detail_cancelled_open_reversal()}
                    </Button>
                  )}
                  <Button
                    variant={reversalPending ? 'ghost' : 'secondary'}
                    size="sm"
                    onClick={() => void nav({ to: '/work-orders/new' })}
                  >
                    <Plus size={16} aria-hidden="true" />
                    {m.route_wo_detail_cancelled_new_order()}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setTab('timeline')}>
                    <History size={16} aria-hidden="true" />
                    {/* Круг 1 (UX): то же слово, что у вкладки, — одно место, одно имя. */}
                    {m.route_wo_detail_tab_timeline()}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {actionsStopped && (
            <div
              id="wo-process-stopped"
              className={styles.pageNotice}
              data-tone="danger"
              role="status"
              tabIndex={-1}
            >
              {/* Круг 4 (UI): danger — `AlertOctagon` (§ 4.1a), треугольник — значок warn. */}
              <AlertOctagon size={16} aria-hidden="true" className={styles.noticeIcon} />
              <div className={styles.noticeBody}>
                <p className={styles.noticeText}>
                  {/* С-I2 Р.1: у `planned` выход есть с карточки — плашка называет его, а не поддержку. */}
                  {stoppedPlanned ? (
                    m.route_wo_detail_process_stopped_planned({ state: woStateTitle(state) })
                  ) : (
                    <>
                      {isAdminViewer
                        ? m.route_wo_detail_process_stopped_admin({ state: woStateTitle(state) })
                        : m.route_wo_detail_process_stopped_operator({
                            state: woStateTitle(state),
                          })}{' '}
                      <span className={styles.noticeRef}>
                        {stoppedTrace ? `${wo.code} · ${stoppedTrace.slice(0, 8)}` : wo.code}
                      </span>
                    </>
                  )}
                </p>
                {/* Круг 8 (UX): после «Проверить снова» видно, что проверка была. */}
                <p className={styles.noticeMeta}>
                  {[
                    stoppedWhen,
                    m.route_wo_detail_checked_at({
                      at: formatDateTime(new Date(woQuery.dataUpdatedAt).toISOString()),
                    }),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                {/* Круг 8: действия оговорки — своим рядом, вместе; в строке со временем «Проверить
                    снова» переносилась одна и висела отдельно. */}
                {/* Круг 1 (UI, MAJOR): первой стоит кнопка с рамкой — ряд у линии текста, без сдвига под
                    Ghost; выход — Destructive, как отмена в ряду действий (§ 4.1, запись уровня 2). */}
                <div
                  className={
                    stoppedPlanned
                      ? `${styles.noticeActions} ${styles.noticeActionsFlush}`
                      : styles.noticeActions
                  }
                >
                  {stoppedPlanned && woCancelMode(state) === 'tier2' && (
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={signalPending}
                      onClick={() => setCancelDlg(true)}
                    >
                      {m.route_wo_detail_stopped_cancel_cta()}
                    </Button>
                  )}
                  {/* Круг 7 (UX): в буфер — готовая записка для поддержки (номер, статус, когда и как
                      остановилось, trace), а не голый номер: иначе поддержка вернётся с вопросами. */}
                  {!stoppedPlanned && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        copyStoppedRef(
                          [
                            wo.code,
                            woStateTitle(state),
                            stoppedWhen,
                            stoppedTrace ? `trace ${stoppedTrace}` : null,
                            // Круг 8 (UX): номер заказа повторяется в разных компаниях — ссылка с UUID
                            // карточки однозначна.
                            `${window.location.origin}/work-orders/${id}`,
                          ]
                            .filter(Boolean)
                            .join(' · '),
                        )
                      }
                    >
                      <Copy size={16} aria-hidden="true" />
                      {m.route_wo_detail_copy_ref()}
                    </Button>
                  )}
                  {/* Круг 7 (UX): поддержка восстановила процесс — статус не меняется и события не придёт;
                      без повтора карточка оставалась красной до перезагрузки. */}
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={woQuery.isFetching || recheckMut.isPending}
                    onClick={() => recheckMut.mutate()}
                  >
                    <RefreshCw size={16} aria-hidden="true" />
                    {m.route_wo_detail_execution_recheck()}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* F104 (NC-55; гейт F110, круг 10, UI MAJOR): у отменённого заказа значения не гасятся
              (`opacity` 0,55 давало ≈ 3,8:1) — неактуален только план, и это сказано словом в плитке.
              Факт — из истории переходов, он по отмене не устаревает. */}
          <div className={styles.metricsGrid}>
            {/* FE-66: plan-* поля = DATE в БД, «00:00» time component нерелевантен.
                actual-* — реальный timestamp, оставляем formatDateTime. */}
            {/* Круг 2 (UX, MAJOR): «план снят отменой» — только когда факта нет; выполненный план снятым
                не называется. */}
            <Metric
              label={m.route_wo_detail_metric_planned_start()}
              value={formatDate(wo.planned_start_at)}
              {...(state === 'cancelled' && !factStart
                ? { note: m.route_wo_detail_metric_plan_void() }
                : {})}
            />
            <Metric
              label={m.route_wo_detail_metric_actual_start()}
              value={formatDateTime(factStart)}
            />
            <Metric
              label={m.route_wo_detail_metric_planned_end()}
              value={formatDate(wo.planned_end_at)}
              {...(state === 'cancelled' && !factEnd
                ? { note: m.route_wo_detail_metric_plan_void() }
                : {})}
            />
            <Metric label={m.route_wo_detail_metric_actual_end()} value={formatDateTime(factEnd)} />
          </div>

          {costVariance && varianceSign && (
            <div
              role="status"
              aria-live="polite"
              className={[
                styles.varianceBanner,
                varianceSign === 'neg' ? styles.varianceBannerDanger : styles.varianceBannerOk,
              ].join(' ')}
            >
              {m.route_wo_detail_variance_banner({
                variance: costVariance,
                pct: wo.cost?.variance_pct?.toFixed(1) ?? '—',
              })}
            </div>
          )}

          <div
            role="toolbar"
            aria-label={m.route_wo_detail_toolbar_aria()}
            className={styles.toolbar}
          >
            {/* F104 (С-I): выпуск — и из черновика (сервер принимает `release` из `draft`). Круг 12 гейта
                F110 снимал его, пока степпер не знал истории; теперь «Запланирован» у такого заказа —
                «пропущен», а не пройден. */}
            {(state === 'planned' || state === 'draft') &&
              (stoppedPlanned ? (
                /* С-I2 Р.1, круг 1 (UI): причина — вплотную к запертому «Выпустить», своей парой,
                   а не на равном расстоянии от доступной «Отменить заказ». */
                <span className={styles.toolbarPair}>
                  <Button variant="primary" {...actionProps} onClick={() => setReleaseDlg(true)}>
                    {m.route_wo_detail_btn_release()}
                  </Button>
                  <span id="wo-actions-stopped" className={styles.toolbarReason}>
                    {m.route_wo_detail_release_stopped()}
                  </span>
                </span>
              ) : (
                <Button
                  variant="primary"
                  {...actionProps}
                  onClick={() => setReleaseDlg(true)}
                  {...shortcutTitle(m.route_wo_detail_shortcut_key_r(), 'r')}
                >
                  {m.route_wo_detail_btn_release()}{' '}
                  {!actionsStopped && (
                    <span className={styles.kbdHint} aria-hidden="true">
                      R
                    </span>
                  )}
                </Button>
              ))}
            {state === 'released' && (
              <Button
                variant="primary"
                {...actionProps}
                onClick={() => startMut.mutate()}
                {...shortcutTitle(m.route_wo_detail_shortcut_key_s(), 's')}
              >
                {m.route_wo_detail_btn_start()}{' '}
                {!actionsStopped && (
                  <span className={styles.kbdHint} aria-hidden="true">
                    S
                  </span>
                )}
              </Button>
            )}
            {state === 'in_progress' && (
              <>
                <Button variant="secondary" {...actionProps} onClick={() => setPauseDlg(true)}>
                  {m.route_wo_detail_btn_pause()}
                </Button>
                <Button
                  variant="primary"
                  {...actionProps}
                  // Круг 13 (UI+UX, MAJOR): клик — через то же подтверждение, что и клавиша «C».
                  onClick={() => setCompleteDlg(true)}
                  // Круг 17 (UI, MAJOR): без складов — выключенная Primary (пунктир, § 4.1), не рабочая
                  // заливка; нажатие ведёт к объяснению с действием «Отменить заказ…».
                  {...(completeBlocked && !actionsStopped
                    ? {
                        disabled: true,
                        focusableWhenDisabled: true,
                        onDisabledClick: () => setCompleteDlg(true),
                        'aria-describedby': 'wo-complete-blocked',
                      }
                    : shortcutTitle(m.route_wo_detail_shortcut_key_c(), 'c'))}
                >
                  {m.route_wo_detail_btn_complete()}{' '}
                  {!actionsStopped && !completeBlocked && (
                    <span className={styles.kbdHint} aria-hidden="true">
                      C
                    </span>
                  )}
                </Button>
                {/* Круги 16–17 (UX): причина запрета — сразу за «Завершить», до клика. */}
                {!actionsStopped && completeBlocked && (
                  <span id="wo-complete-blocked" className={styles.toolbarReason}>
                    {m.route_wo_detail_complete_blocked_reason()}
                  </span>
                )}
              </>
            )}
            {state === 'paused_external' && (
              <Button
                variant="primary"
                {...actionProps}
                onClick={() => resumeMut.mutate()}
                {...shortcutTitle(m.route_wo_detail_shortcut_key_s(), 's')}
              >
                {m.route_wo_detail_btn_resume()}{' '}
                {!actionsStopped && (
                  <span className={styles.kbdHint} aria-hidden="true">
                    S
                  </span>
                )}
              </Button>
            )}
            {state === 'completed' && (
              <Button
                variant="secondary"
                onClick={() =>
                  toast.push({ variant: 'info', title: m.route_wo_detail_toast_close_wip() })
                }
              >
                {m.route_wo_detail_btn_close()}
              </Button>
            )}
            {woCancelMode(state) === 'tier2' && (
              <Button
                variant="destructive"
                {...(stoppedPlanned ? { disabled: signalPending } : actionProps)}
                onClick={() => setCancelDlg(true)}
              >
                {/* Круг 1 (UX): то же имя, что у выхода в оговорке. */}
                {stoppedPlanned
                  ? m.route_wo_detail_stopped_cancel_cta()
                  : m.route_wo_detail_btn_cancel_t2()}
              </Button>
            )}
            {/* Круг 3 гейта (UI): причина выключения — вплотную к кнопкам (§ 4.1), подробности — в
                оговорке выше; на неё ведёт клик по выключенной кнопке. */}
            {actionsStopped && !stoppedPlanned && (
              <span id="wo-actions-stopped" className={styles.toolbarReason}>
                {m.route_wo_detail_actions_stopped()}
              </span>
            )}
            {/* Круг 4 (UX): Temporal не ответил — признак неизвестен; кнопки остаются, но карточка
                не делает вид, что всё в порядке. */}
            {wo?.execution_stopped === null && (
              <span className={styles.toolbarUnknown}>
                <Hourglass size={16} aria-hidden="true" />
                {m.route_wo_detail_execution_unknown({
                  at: formatDateTime(new Date(woQuery.dataUpdatedAt).toISOString()),
                })}
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={woQuery.isFetching || recheckMut.isPending}
                  onClick={() => recheckMut.mutate()}
                >
                  <RefreshCw size={16} aria-hidden="true" />
                  {m.route_wo_detail_execution_recheck()}
                </Button>
              </span>
            )}
            {/* F90 (Ф6), С-G Р.5: отмена завершённого и закрытого заказа — заявка уровня 3:
                Руководитель производства подписывает первым, Руководитель компании или проектов —
                вторым. */}
            {woCancelMode(state) === 'tier3' && (
              <Tier3SendAction
                variant="destructive"
                label={m.route_wo_detail_btn_cancel_t3()}
                subject={{
                  taskType: 'work_order_cancel_completed',
                  subjectKind: 'work_order',
                  payload: { work_order_id: id },
                }}
                reasonField={{
                  key: 'reason',
                  label: m.route_wo_detail_cancel_t3_reason_label(),
                  placeholder: m.route_wo_detail_cancel_t3_reason_placeholder(),
                  minLength: 10,
                }}
              />
            )}
            {/* F104 (NC-55): у закрытого заказа подписи «Только чтение» нет — отмена заявкой уровня 3 у него
                есть, и подпись спорила с кнопкой рядом. */}
          </div>

          <div
            role="tablist"
            aria-label={m.route_wo_detail_tabs_aria()}
            aria-orientation="horizontal"
            className={styles.tabs}
            onKeyDown={(e) => {
              const idx = TAB_IDS.findIndex((tid) => tid === tab);
              if (idx === -1) return;
              let next = idx;
              if (e.key === 'ArrowRight') next = (idx + 1) % TAB_IDS.length;
              else if (e.key === 'ArrowLeft') next = (idx - 1 + TAB_IDS.length) % TAB_IDS.length;
              else if (e.key === 'Home') next = 0;
              else if (e.key === 'End') next = TAB_IDS.length - 1;
              else return;
              e.preventDefault();
              const nextTab = TAB_IDS[next];
              if (nextTab) {
                setTab(nextTab);
                document.getElementById(`wo-tab-${nextTab}`)?.focus();
              }
            }}
          >
            {tabsList().map((t) => (
              <button
                key={t.id}
                id={`wo-tab-${t.id}`}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                aria-controls={`wo-panel-${t.id}`}
                tabIndex={tab === t.id ? 0 : -1}
                onClick={() => setTab(t.id)}
                className={[styles.tabBtn, tab === t.id ? styles.tabBtnActive : '']
                  .filter(Boolean)
                  .join(' ')}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div
            role="tabpanel"
            id={`wo-panel-${tab}`}
            aria-labelledby={`wo-tab-${tab}`}
            className={styles.tabPanel}
          >
            {tab === 'overview' && (
              <dl className={styles.detailGrid}>
                <dt>{m.route_wo_detail_field_bom()}</dt>
                <dd>
                  <a href={`/boms/${wo.bom_id}`} title={wo.bom_code ?? undefined}>
                    {wo.bom_name
                      ? m.route_wo_detail_bom_label_named({
                          name: wo.bom_name,
                          version: String(wo.bom_version ?? '—'),
                        })
                      : m.route_wo_detail_bom_label_short({
                          id: wo.bom_id.slice(0, 8),
                          version: String(wo.bom_version ?? '—'),
                        })}
                  </a>
                </dd>
                <dt>{m.route_wo_detail_field_executor()}</dt>
                <dd>
                  {wo.executor_id ? (
                    <a href={`/admin/users?highlight=${wo.executor_id}`}>
                      {wo.executor_name ?? wo.executor_email ?? wo.executor_id.slice(0, 8)}
                    </a>
                  ) : (
                    '—'
                  )}
                </dd>
                {wo.source_warehouse_id && (
                  <>
                    <dt>{m.route_wo_detail_field_source_wh()}</dt>
                    <dd>
                      <a href={`/warehouses#${wo.source_warehouse_id}`}>
                        {wo.source_warehouse_name ?? wo.source_warehouse_id.slice(0, 8)}
                      </a>
                    </dd>
                  </>
                )}
                <dt>{m.route_wo_detail_field_wip_wh()}</dt>
                <dd>
                  {wo.wip_warehouse_id ? (
                    <a href={`/warehouses#${wo.wip_warehouse_id}`}>
                      {wo.wip_warehouse_name ?? wo.wip_warehouse_id.slice(0, 8)}
                    </a>
                  ) : missingWarehouseNote ? (
                    <span className={styles.missingValue}>{missingWarehouseNote}</span>
                  ) : (
                    '—'
                  )}
                </dd>
                <dt>{m.route_wo_detail_field_fg_wh()}</dt>
                <dd>
                  {wo.fg_warehouse_id ? (
                    <a href={`/warehouses#${wo.fg_warehouse_id}`}>
                      {wo.fg_warehouse_name ?? wo.fg_warehouse_id.slice(0, 8)}
                    </a>
                  ) : missingWarehouseNote ? (
                    <span className={styles.missingValue}>{missingWarehouseNote}</span>
                  ) : (
                    '—'
                  )}
                </dd>
                {wo.scrap_warehouse_id && (
                  <>
                    <dt>{m.route_wo_detail_field_scrap_wh()}</dt>
                    <dd>
                      <a href={`/warehouses#${wo.scrap_warehouse_id}`}>
                        {wo.scrap_warehouse_name ?? wo.scrap_warehouse_id.slice(0, 8)}
                      </a>
                    </dd>
                  </>
                )}
                <dt>{m.route_wo_detail_field_warnings()}</dt>
                <dd>
                  {(wo.warnings ?? []).length > 0 ? (
                    <button
                      type="button"
                      onClick={() => setTab('warnings')}
                      className={shared.link}
                    >
                      {m.route_wo_detail_warnings_value({ count: (wo.warnings ?? []).length })}
                    </button>
                  ) : (
                    '0'
                  )}
                </dd>
                <dt>{m.route_wo_detail_field_progress()}</dt>
                <dd>
                  {/* FE-225 Pass 22: closed/completed → 100%, cancelled → N/A,
                      иначе — SSE progress real-time. Раньше «0%» при closed
                      WO. */}
                  {state === 'completed' || state === 'closed'
                    ? m.route_wo_detail_progress_value({ pct: '100' })
                    : state === 'cancelled'
                      ? '—'
                      : m.route_wo_detail_progress_value({ pct: sseState.progress.toFixed(0) })}
                </dd>
              </dl>
            )}
            {tab === 'materials' && <MaterialsTab workOrderId={id} />}
            {tab === 'job_cards' && (
              <JobCardsTable
                workOrderId={id}
                jobCards={wo.job_cards ?? []}
                readOnly={state === 'closed' || state === 'cancelled'}
              />
            )}
            {tab === 'warnings' && (
              <WoWarningsPanel
                warnings={warningsBundleQuery.data?.warnings ?? wo.warnings ?? []}
                bundle={
                  warningsBundleQuery.data
                    ? {
                        items: warningsBundleQuery.data.items,
                        ...(warningsBundleQuery.data.batches
                          ? { batches: warningsBundleQuery.data.batches }
                          : {}),
                      }
                    : undefined
                }
              />
            )}
            {tab === 'timeline' &&
              (timelineQuery.isError ? (
                <ErrorState
                  error={timelineQuery.error as Error}
                  onRetry={() => timelineQuery.refetch()}
                />
              ) : timelineQuery.isLoading ? (
                <Skeleton />
              ) : (
                <WoTimeline
                  entries={timelineItems}
                  onOpenAudit={(eventId) => setTab('audit', eventId)}
                />
              ))}
            {tab === 'audit' && (
              <EntityAuditPanel
                entity_type="work_order"
                entity_id={id}
                highlight={search.highlight}
                embedded
                onBack={search.highlight ? () => setTab('timeline') : undefined}
              />
            )}
            {tab === 'sles' &&
              (slesQuery.isError ? (
                <ErrorState error={slesQuery.error as Error} onRetry={() => slesQuery.refetch()} />
              ) : slesQuery.isLoading ? (
                <Skeleton />
              ) : (
                <Table
                  ariaLabel={m.route_wo_detail_sles_aria()}
                  data={sles?.items ?? []}
                  rowKey={(s) => s.id}
                  columns={[
                    {
                      id: 'id',
                      header: m.route_wo_detail_col_sle_id(),
                      accessor: (s) => s.id.slice(0, 8),
                      mono: true,
                    },
                    {
                      id: 'at',
                      header: m.route_wo_detail_col_time(),
                      accessor: (s) => formatDateTime(s.posting_at),
                      mono: true,
                    },
                    {
                      id: 'item',
                      header: m.route_wo_detail_col_item(),
                      accessor: (s) =>
                        s.item_name
                          ? s.item_sku
                            ? m.route_wo_detail_item_label_dual({
                                name: s.item_name,
                                sku: s.item_sku,
                              })
                            : s.item_name
                          : s.item_id.slice(0, 8),
                    },
                    {
                      id: 'wh',
                      header: m.route_wo_detail_col_wh(),
                      accessor: (s) => s.warehouse_name ?? s.warehouse_id.slice(0, 8),
                    },
                    {
                      id: 'qty',
                      header: m.route_wo_detail_col_qty_delta(),
                      accessor: (s) => s.qty_delta,
                      mono: true,
                      align: 'right',
                    },
                    {
                      id: 'rate',
                      header: m.route_wo_detail_col_rate(),
                      accessor: (s) => s.valuation_rate ?? '—',
                      mono: true,
                      align: 'right',
                    },
                  ]}
                />
              ))}
          </div>
        </>
      )}

      {/* Release dialog (with manufacture preview). U10.1 — передаём override/alternates. */}
      <ManufacturePreviewDialog
        open={releaseDlg}
        onClose={() => setReleaseDlg(false)}
        preview={preview ?? null}
        loading={previewLoading}
        allowOverride
        submitting={releaseMut.isPending}
        confirmLabel={m.route_wo_detail_release_confirm()}
        onConfirm={(opts) => releaseMut.mutate(opts)}
      />

      <Dialog
        open={cancelDlg}
        onClose={closeCancelDlg}
        title={m.route_wo_detail_cancel_t2_title({ code: wo?.code ?? id })}
        footer={
          <>
            {/* F110 круг 1 (UI): кнопки — в подвале `Dialog` (§ 4.1), причина выключения — подписью
                слева; «Не отменять» вместо «Отмена» рядом с «Подтвердить отмену». */}
            {cancelBlockedReason && (
              <span id="wo-cancel-reason" className={styles.footerReason}>
                {cancelBlockedReason}
              </span>
            )}
            {/* Круг 5 (UI): в необратимом диалоге безопасное действие — Secondary (§ 4.1, как у
                `ConfirmDialog` danger), не Ghost слабее красной кнопки. */}
            <Button variant="secondary" onClick={closeCancelDlg}>
              {m.route_wo_detail_cancel_t2_cancel()}
            </Button>
            <Button
              variant="destructive"
              disabled={cancelBlockedReason !== null || cancelTier2Mut.isPending}
              focusableWhenDisabled
              onDisabledClick={() =>
                document
                  .getElementById(
                    cancelReason.trim().length < 10
                      ? 'wo-cancel-reason-input'
                      : 'wo-cancel-approver',
                  )
                  ?.focus()
              }
              aria-describedby={cancelBlockedReason ? 'wo-cancel-reason' : undefined}
              onClick={() => cancelTier2Mut.mutate(cancelReason.trim())}
            >
              {m.route_wo_detail_cancel_t2_confirm()}
            </Button>
          </>
        }
      >
        <div className={styles.cancelForm}>
          {/* Круг 4 (UX): что будет после отмены — до того, как её подтвердят. */}
          <p className={styles.dialogLead}>{m.route_wo_detail_cancel_consequence()}</p>
          {/* Круг 5 (UX): процесс не ответил — отмена уходит сигналом и может не пройти. */}
          {/* Круг 6 (UI): оговорка к блоку грейда `--warn` (§ 4.1b) — плёнка, рельс, значок. */}
          {wo?.execution_stopped === null && (
            <div className={styles.dialogWarn}>
              <AlertTriangle size={16} aria-hidden="true" className={styles.noticeIcon} />
              <p className={styles.noticeText}>{m.route_wo_detail_cancel_unknown_warning()}</p>
            </div>
          )}
          <Input
            id="wo-cancel-reason-input"
            data-autofocus="true"
            label={m.route_wo_detail_cancel_reason_label()}
            value={cancelReason}
            required
            onChange={(e) => setCancelReason(e.target.value)}
          />
          {/* Круг 3 гейта (UI+UX): `Combobox` — подпись «имя · роль» целиком, e-mail строкой ниже
              (`optionStacked`), поиск по имени и e-mail — на сервере. */}
          <Combobox
            // Диалог не размонтирует содержимое: без смены ключа строка поиска и раскрытый список
            // переживали «Не отменять» и встречали при следующем открытии.
            key={cancelDlg ? 'open' : 'closed'}
            className={styles.approverField}
            id="wo-cancel-approver"
            label={m.route_wo_detail_cancel_t2_approver_label()}
            value={cancelApproverId}
            onChange={setCancelApproverId}
            loadOptions={loadApprovers}
            placeholder={m.route_wo_detail_cancel_approver_placeholder()}
            noResultsText={m.route_wo_detail_cancel_approver_not_found()}
            required
            disabled={approversQuery.isLoading}
            {...(approversQuery.data
              ? {
                  // Круг 6 (UX): до выбора — кто вправе согласовать; после — кого выбрали и что
                  // попадёт в журнал (правила выбора уже не помогают решению). Высота подсказки
                  // зарезервирована (`.approverField`), диалог при выборе не прыгает (UI).
                  hint: (cancelApprover
                    ? [
                        cancelApprover.full_name
                          ? m.route_wo_detail_cancel_approver_selected({
                              email: cancelApprover.email,
                            })
                          : '',
                        m.route_wo_detail_cancel_approver_journal(),
                      ]
                    : [
                        m.route_wo_detail_cancel_t2_approver_hint({ roles: approverRoles }),
                        actorIsCandidate ? m.route_wo_detail_cancel_self_note() : '',
                      ]
                  )
                    .filter(Boolean)
                    .join(' '),
                }
              : {})}
            {...(approversQuery.isError
              ? { error: m.route_wo_detail_cancel_approvers_failed() }
              : approversQuery.isSuccess && cancelApprovers.length === 0
                ? { error: m.route_wo_detail_cancel_no_approvers({ roles: approverRoles }) }
                : {})}
          />
          {approversQuery.isError && (
            <Button variant="ghost" size="sm" onClick={() => void approversQuery.refetch()}>
              {m.common_button_retry()}
            </Button>
          )}
        </div>
      </Dialog>

      <ConfirmDialog
        open={pauseDlg}
        title={m.route_wo_detail_pause_title()}
        description={
          pauseMut.isError
            ? m.route_wo_detail_pause_error({ message: pauseMut.error.message })
            : m.route_wo_detail_pause_description()
        }
        confirmLabel={
          pauseMut.isPending
            ? m.route_wo_detail_pause_confirm_pending()
            : m.route_wo_detail_pause_confirm()
        }
        withInput
        inputLabel={m.route_wo_detail_pause_reason_label()}
        inputPlaceholder={m.route_wo_detail_pause_reason_placeholder()}
        inputRequired
        onConfirm={(reason) => {
          if (reason) pauseMut.mutate(reason);
        }}
        onCancel={() => {
          pauseMut.reset();
          setPauseDlg(false);
        }}
      />

      {/* F110 круг 12 (UX, BLOCKER): завершение — только после подтверждения (кнопка и клавиша «C»):
          вернуть закрытый заказ можно лишь заявкой уровня 3. Круг 16 (UI+UX): без складов — жёсткий
          стоп: заголовок «… нельзя», причина первой, без обещаний последствий, одна кнопка «Закрыть» и
          действие «Отменить заказ…» в самой оговорке. */}
      <ConfirmDialog
        open={completeDlg}
        title={
          completeBlocked
            ? m.route_wo_detail_complete_blocked_title({ code: wo?.code ?? id })
            : m.route_wo_detail_complete_confirm_title({ code: wo?.code ?? id })
        }
        meta={
          wo && !completeBlocked ? (
            <span className={styles.confirmMetaLabel}>
              {m.route_wo_detail_complete_confirm_meta({
                item: wo.item_name ?? wo.item_sku ?? '—',
              })}{' '}
              <span className={styles.confirmMeta}>{formatQty(wo.qty, wo.item_uom)}</span>
            </span>
          ) : undefined
        }
        description={
          completeBlocked ? (
            <div className={styles.dialogBlocked}>
              <AlertOctagon size={16} aria-hidden="true" className={styles.noticeIcon} />
              <div className={styles.noticeBody}>
                <p className={styles.noticeText}>
                  {m.route_wo_detail_complete_confirm_blocked_warehouses({
                    warehouses: missingWarehouses.join(', '),
                  })}
                </p>
                <div className={styles.noticeActions}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCompleteDlg(false);
                      setCancelDlg(true);
                    }}
                  >
                    {m.route_wo_detail_complete_blocked_cancel_order()}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.confirmBody}>
              <p className={styles.noticeText}>
                {wo?.fg_warehouse_name
                  ? m.route_wo_detail_complete_confirm_effect_wh({
                      qty: formatQty(wo.qty, wo.item_uom),
                      warehouse: wo.fg_warehouse_name,
                      bom: nbHyphen(wo.bom_name ?? wo.bom_code ?? '—'),
                    })
                  : m.route_wo_detail_complete_confirm_effect({
                      qty: formatQty(wo?.qty, wo?.item_uom),
                      bom: nbHyphen(wo?.bom_name ?? wo?.bom_code ?? '—'),
                    })}
              </p>
              <p className={styles.noticeText}>
                {m.route_wo_detail_complete_confirm_description()}
              </p>
              {wo && (wo.warnings ?? []).length > 0 && (
                <div className={styles.dialogWarn}>
                  <AlertTriangle size={16} aria-hidden="true" className={styles.noticeIcon} />
                  <div className={styles.noticeBody}>
                    <p className={styles.noticeText}>
                      {m.route_wo_detail_complete_confirm_warn_warnings({
                        count: (wo.warnings ?? []).length,
                      })}
                    </p>
                    <div className={styles.noticeActions}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setCompleteDlg(false);
                          setTab('warnings');
                          // Круг 16 (UI): фокус — на вкладку, куда перевели (при открытии клавишей «C»
                          // кнопки-источника нет, и фокус уходил на body).
                          requestAnimationFrame(() =>
                            document.getElementById('wo-tab-warnings')?.focus(),
                          );
                        }}
                      >
                        {m.route_wo_detail_complete_confirm_open_warnings()}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        }
        confirmLabel={m.route_wo_detail_btn_complete()}
        hideConfirm={completeBlocked}
        cancelLabel={
          completeBlocked
            ? m.route_wo_detail_complete_blocked_close()
            : m.route_wo_detail_complete_confirm_cancel()
        }
        // Необратимо — фокус и вес на безопасном «Не завершать»: Enter сразу после «C» не завершает.
        emphasizeCancel
        size="md"
        onConfirm={() => {
          setCompleteDlg(false);
          completeMut.mutate();
        }}
        onCancel={() => setCompleteDlg(false)}
      />

      <Dialog
        open={shortcutHelpOpen}
        onClose={() => setShortcutHelpOpen(false)}
        title={m.route_wo_detail_shortcut_help_title()}
      >
        <dl className={styles.shortcutDl}>
          <div>
            <dt className={styles.shortcutDt}>
              <kbd>R</kbd>
            </dt>
            <dd>{m.route_wo_detail_shortcut_r_desc()}</dd>
          </div>
          <div>
            <dt className={styles.shortcutDt}>
              <kbd>S</kbd>
            </dt>
            <dd>{m.route_wo_detail_shortcut_s_desc()}</dd>
          </div>
          <div>
            <dt className={styles.shortcutDt}>
              <kbd>C</kbd>
            </dt>
            <dd>{m.route_wo_detail_shortcut_c_desc()}</dd>
          </div>
          <div>
            <dt className={styles.shortcutDt}>
              <kbd>←</kbd> <kbd>→</kbd> <kbd>Home</kbd> <kbd>End</kbd>
            </dt>
            <dd>{m.route_wo_detail_shortcut_arrows_desc()}</dd>
          </div>
          <div>
            <dt className={styles.shortcutDt}>
              <kbd>?</kbd>
            </dt>
            <dd>{m.route_wo_detail_shortcut_help_desc()}</dd>
          </div>
        </dl>
      </Dialog>
    </div>
  );
}

function Metric({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  /** F104 (NC-55): оговорка значения словом — вместо приглушения, которое не читается. */
  note?: string;
}): JSX.Element {
  return (
    <div className={styles.metric}>
      <div className={styles.metricLabel}>{label}</div>
      <div className={styles.metricValue}>{value}</div>
      {note && <div className={styles.metricNote}>{note}</div>}
    </div>
  );
}

/**
 * U11.6 — Materials tab: человекочитаемые имена + SKU + UoM (W10.3 BE enrichment).
 */
function MaterialsTab({ workOrderId }: { workOrderId: string }): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['wo-preview-materials', workOrderId],
    queryFn: () => fetchManufacturePreview(workOrderId),
  });

  if (isLoading) return <p>{m.route_wo_detail_materials_loading()}</p>;
  if (!data) return <p className={shared.subtle}>{m.route_wo_detail_materials_empty()}</p>;

  const rows = data.requirements ?? [];

  return (
    <Table
      ariaLabel={m.route_wo_detail_materials_aria()}
      data={rows}
      rowKey={(r) => r.item_id}
      columns={[
        {
          id: 'item',
          header: m.route_wo_detail_col_item(),
          accessor: (r) => {
            const name = r.item_name ?? r.item_code ?? null;
            const sku = r.item_sku ?? r.item_code ?? null;
            const label =
              name && sku && name !== sku
                ? m.route_wo_detail_item_label_dual({ name, sku })
                : (name ?? sku ?? r.item_id.slice(0, 8));
            return <a href={`/items/${r.item_id}`}>{label}</a>;
          },
        },
        {
          id: 'req',
          header: m.route_wo_detail_col_req(),
          accessor: (r) => formatQty(r.qty_required ?? r.required_qty, r.uom),
          mono: true,
          align: 'right',
        },
        {
          id: 'avail',
          header: m.route_wo_detail_col_avail(),
          accessor: (r) => formatQty(r.qty_available, r.uom),
          mono: true,
          align: 'right',
        },
        {
          id: 'shortage',
          header: m.route_wo_detail_col_shortage(),
          align: 'right',
          mono: true,
          accessor: (r) =>
            Number(r.shortage ?? 0) > 0 ? (
              <span className={shared.dangerMono}>−{formatQty(r.shortage, r.uom)}</span>
            ) : (
              '—'
            ),
        },
        {
          id: 'src',
          header: m.route_wo_detail_col_src_wh(),
          accessor: (r) => r.from_warehouse_name ?? '—',
        },
        {
          id: 'batch',
          header: m.route_wo_detail_col_batch(),
          accessor: (r) => r.batch_no ?? '—',
        },
      ]}
    />
  );
}
